/* Archive lightbox (snippets/archive-gallery.liquid). Built on anime.js v3, like assets/motion.js.
   open:  the clicked look lifts off the grid and grows to the centre of the viewport while the page fades to the
          background colour; the controls follow. Closing mid-flight turns it around from wherever it is.
   move:  the look on stage slides out to one side as the next slides in from the other: the arrows, the keys, a click
          either side of the look (arrow cursors), or on touch a swipe that follows the finger and lets the look leave
          the way it was pushed. The neighbours are fetched ahead at lightbox size.
   close: ×, Escape, a mouse click on the look, or on touch a pull up or down (a finger's tap on the look moves on instead: its halves are the easy targets). The controls go first, then the look flies
          back into its cell as the page returns. If that cell is out of view (the visitor moved on to other looks) the
          page is scrolled to it, unseen, behind the lightbox. The grid image is revealed under the landed look and
          given a frame to paint before the lightbox goes, so the handover is invisible.
   A look is shown from the grid's own 800px file at once, then swapped for one sized to the screen once that has
   decoded. Reduced motion, or no anime: the lightbox simply appears and disappears. */
(() => {
  const dialog = document.querySelector('[data-lightbox]');
  const gallery = document.querySelector('[data-lightbox-gallery]');
  if (!dialog || !gallery) return;
  const items = [...gallery.querySelectorAll('[data-lightbox-open]')].map((button) => ({ button, img: button.querySelector('img') })).filter((item) => item.img);
  if (!items.length) return;
  const stage = dialog.querySelector('[data-lightbox-stage]');
  const bg = dialog.querySelector('[data-lightbox-bg]');
  const ui = [...dialog.querySelectorAll('[data-lightbox-ui]')];
  const counter = dialog.querySelector('[data-lightbox-index]');
  dialog.querySelector('[data-lightbox-total]').textContent = items.length;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animate = () => typeof window.anime === 'function' && !reduced;
  const EASE_IN = 'cubicBezier(0.2, 0.7, 0.2, 1)';  // arrivals (the page entrances use the same curve)
  const EASE_OUT = 'cubicBezier(0.4, 0, 0.2, 1)';   // departures
  const SPRING_SNAPPY = 'spring(1, 320, 26, 0)';

  let phase = null;   // 'opening' | 'open' | 'closing' | null (closed)
  let gen = 0;        // bumped by every open / close: a superseded choreography's ending is ignored
  let index = -1;     // the look on stage
  let current = null; // its <img>; current.base is its untransformed rect, read when it was placed
  let lifted = -1;    // the grid cell whose look is on stage (shows its placeholder meanwhile)
  let dragEnd = 0;    // when the last real drag ended: the click it releases must not act
  let pointerType = 'mouse'; // of the last press on the stage: a finger's tap beside the look moves on with the swipe motion
  const sharp = new Map(); // index → Image sized for this screen

  /** Resolve when the animation finishes, or after a timeout (anime pauses in hidden tabs) — then force the end state. */
  const settle = (anim, ms) => Promise.race([anim.finished, new Promise((r) => setTimeout(r, ms))]).then(() => {
    if (anim.progress < 100) { anim.pause(); anim.seek(anim.duration); }
  });
  const nextFrames = (n) => new Promise((r) => { const step = () => (n-- > 0 ? requestAnimationFrame(step) : r()); step(); });

  const ratioOf = (img) => (img.naturalWidth && img.naturalHeight
    ? img.naturalWidth / img.naturalHeight
    : (Number(img.getAttribute('width')) || 4) / (Number(img.getAttribute('height')) || 5));

  /** The grid's file at a width for this screen; Shopify's CDN resizes on request. Files it can't resize keep the grid's URL. */
  const sharpUrl = (i, px) => {
    const full = items[i].button.dataset.full;
    if (!full) return null;
    const url = new URL(full, location.href);
    url.searchParams.set('width', Math.min(2000, Math.max(400, Math.ceil(px / 100) * 100)));
    return url.href;
  };
  const dpr = () => window.devicePixelRatio || 1;

  const lift = (i) => {
    if (lifted >= 0) items[lifted].button.classList.remove('is-lifted');
    lifted = i;
    if (i >= 0) items[i].button.classList.add('is-lifted');
  };

  const makeLook = (i) => {
    const thumb = items[i].img;
    const img = document.createElement('img');
    img.className = 'lightbox__image is-loaded';
    img.alt = thumb.alt;
    img.draggable = false;
    img.style.setProperty('--r', ratioOf(thumb));
    const ready = sharp.get(i);
    img.src = ready && ready.complete && ready.naturalWidth ? ready.src : (thumb.currentSrc || thumb.src);
    return img;
  };
  const place = (img) => { stage.append(img); img.base = img.getBoundingClientRect(); return img; }; // measured before any transform
  const clearStage = () => stage.querySelectorAll('.lightbox__image').forEach((el) => el.remove()); // the side zones stay
  const transform = (x, y, s = 1) => `translateX(${x}px) translateY(${y}px) scale(${s})`; // the order anime writes: fit() relies on it

  /** Fetch look i at lightbox size; once decoded, put it on stage if look i is still showing. */
  const sharpen = (i, img) => {
    const url = sharpUrl(i, img.base.width * dpr());
    if (!url) return;
    let hi = sharp.get(i);
    if (!hi) { hi = new Image(); hi.src = url; sharp.set(i, hi); }
    if (hi.src === img.src) return;
    const swap = () => {
      if (current !== img || phase === 'closing' || !hi.naturalWidth) return;
      img.style.setProperty('--r', hi.naturalWidth / hi.naturalHeight); // a grid image that hadn't loaded yet had a guessed ratio
      img.src = hi.src;
    };
    (hi.decode ? hi.decode() : Promise.reject()).then(swap, () => {
      if (hi.complete) swap(); else hi.addEventListener('load', swap, { once: true });
    });
  };
  const prefetchAround = () => {
    if (!current || items.length < 2) return;
    const h = current.base.height; // the stage's height cap, near enough for the neighbours
    [index + 1, index - 1].forEach((j) => {
      j = (j + items.length) % items.length;
      if (sharp.has(j)) return;
      const url = sharpUrl(j, Math.min(h * ratioOf(items[j].img), window.innerWidth) * dpr());
      if (!url) return;
      const im = new Image();
      im.src = url;
      sharp.set(j, im);
    });
  };

  /** translate + scale (origin 0 0) that lays `img` exactly over `rect`, whatever transform it carries now. */
  const fit = (img, rect) => ({ x: rect.left - img.base.left, y: rect.top - img.base.top, s: rect.width / img.base.width });

  const open = (i) => {
    if (phase) return;
    const g = ++gen;
    phase = 'opening';
    index = i;
    const thumb = items[i].img;
    clearStage();
    const img = place(makeLook(i));
    current = img;
    counter.textContent = i + 1;
    if (!animate()) {
      bg.style.opacity = '';
      dialog.showModal();
      img.base = img.getBoundingClientRect();
      phase = 'open';
      sharpen(i, img);
      prefetchAround();
      return;
    }
    bg.style.opacity = 0;
    ui.forEach((el) => { el.style.opacity = 0; });
    dialog.showModal();
    lift(i);
    img.base = img.getBoundingClientRect(); // read after showModal (laid out only now; the scroll lock can shift the page)
    const from = fit(img, thumb.getBoundingClientRect());
    anime.remove([bg, img, ...ui]);
    const tl = anime.timeline({ autoplay: true });
    tl.add({ targets: img, translateX: [from.x, 0], translateY: [from.y, 0], scale: [from.s, 1], duration: 320, easing: EASE_IN }, 0);
    tl.add({ targets: bg, opacity: [0, 1], duration: 260, easing: 'easeOutQuad' }, 0);
    tl.add({ targets: ui, opacity: [0, 1], duration: 220, easing: 'easeOutQuad' }, 100);
    settle(tl, 800).then(() => { if (g !== gen) return; phase = 'open'; ui.forEach((el) => { el.style.opacity = ''; }); });
    sharpen(i, img);
    prefetchAround();
  };

  const close = () => {
    if (phase !== 'opening' && phase !== 'open') return;
    const g = ++gen;
    phase = 'closing';
    const img = current;
    if (!animate() || !img) { dialog.close(); return; }
    // the cell may be off screen after moving between looks: bring it into view, behind the lightbox
    const thumb = items[index].img;
    const r = thumb.getBoundingClientRect();
    const header = document.querySelector('.header')?.offsetHeight || 0;
    if (r.top < header || r.bottom > window.innerHeight) thumb.scrollIntoView({ block: 'center', behavior: 'instant' });
    lift(index);
    anime.remove([bg, img, ...ui]);
    const to = fit(img, thumb.getBoundingClientRect());
    const tl = anime.timeline({ autoplay: true });
    // no start values: each picks up from wherever the opening flight, a slide or a pull left it
    tl.add({ targets: ui, opacity: 0, duration: 100, easing: 'easeOutQuad' }, 0);
    tl.add({ targets: img, translateX: to.x, translateY: to.y, scale: to.s, duration: 260, easing: EASE_OUT }, 0);
    tl.add({ targets: bg, opacity: 0, duration: 220, easing: 'easeInQuad' }, 60);
    settle(tl, 800).then(async () => {
      if (g !== gen) return;
      lift(-1); // the grid image is back, exactly under the landed look …
      await thumb.decode().catch(() => {});
      await nextFrames(2); // … and has painted before the look goes
      if (g === gen) dialog.close();
    });
  };
  // closed by any route (the choreography, a second Escape, a bfcache restore): tidy up and hand focus back to the cell
  dialog.addEventListener('close', () => {
    gen += 1;
    phase = null;
    lift(-1);
    clearStage();
    current = null;
    ui.forEach((el) => { el.style.opacity = ''; });
    bg.style.opacity = '';
    if (index >= 0) items[index].button.focus({ preventScroll: true });
  });

  /** Show the neighbour in direction dir. A swipe, or a tap beside the look, carries the look on stage right off the screen; the arrows, keys and mouse clicks shift it a little. */
  const go = (dir, { swipe = false } = {}) => {
    if (phase !== 'open' || items.length < 2) return;
    const next = (index + dir + items.length) % items.length;
    const out = current;
    const img = place(makeLook(next));
    index = next;
    current = img;
    counter.textContent = next + 1;
    lift(next);
    if (!animate()) {
      out.remove();
    } else {
      anime.remove([out, img]);
      if (swipe) {
        const exit = dir > 0 ? -(out.base.left + out.base.width + 40) : window.innerWidth - out.base.left + 40; // fully off screen, the way it was pushed
        anime({ targets: out, translateX: exit, duration: 300, easing: 'easeOutCubic', complete: () => out.remove() });
        anime({ targets: out, opacity: 0, duration: 300, easing: 'easeInQuad' });
        anime({ targets: img, translateX: [dir * Math.round(window.innerWidth * 0.35), 0], duration: 360, easing: EASE_IN });
        anime({ targets: img, opacity: [0, 1], duration: 240, easing: 'easeOutQuad' });
      } else {
        const shift = Math.round(Math.min(80, Math.max(32, window.innerWidth * 0.04)));
        anime({ targets: out, translateX: -dir * shift, opacity: 0, duration: 200, easing: 'easeOutQuad', complete: () => out.remove() });
        anime({ targets: img, translateX: [dir * shift, 0], easing: SPRING_SNAPPY });
        anime({ targets: img, opacity: [0, 1], duration: 260, easing: 'easeOutQuad' });
      }
    }
    sharpen(next, img);
    prefetchAround();
  };

  /* Touch and pen: the look follows the finger. Sideways, a flick or a good push moves on to the neighbour and the look
     leaves in that direction; up or down, the page shows through as it goes, and a good pull closes. Anything less springs back. */
  let drag = null;
  stage.addEventListener('pointerdown', (e) => {
    pointerType = e.pointerType;
    if (e.pointerType === 'mouse' || phase !== 'open' || !current) return;
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), axis: null, dx: 0, dy: 0 };
  });
  stage.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id || !current) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (!drag.axis) {
      if (Math.hypot(dx, dy) < 12) return; // a thumb's tap wobbles a little
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      try { stage.setPointerCapture(e.pointerId); } catch (err) {}
      if (animate()) anime.remove([current, bg]);
    }
    if (drag.axis === 'x') {
      drag.dx = dx;
      current.style.transform = transform(dx, 0);
    } else {
      drag.dy = dy;
      current.style.transform = transform(0, dy);
      bg.style.opacity = Math.max(0.4, 1 - Math.abs(dy) / 480);
    }
  });
  const endDrag = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const d = drag;
    drag = null;
    if (!d.axis || !current) return;
    if (Math.abs(d.dx) > 24 || Math.abs(d.dy) > 24) dragEnd = performance.now(); // a wobbly tap still counts as a tap
    const dt = Math.max(1, performance.now() - d.t);
    if (d.axis === 'x') {
      if (Math.abs(d.dx) > 60 || Math.abs(d.dx / dt) > 0.5) go(d.dx < 0 ? 1 : -1, { swipe: true });
      else if (animate()) anime({ targets: current, translateX: 0, duration: 300, easing: EASE_IN });
      else current.style.transform = '';
    } else if (Math.abs(d.dy) > 80 || Math.abs(d.dy / dt) > 0.5) {
      close();
    } else if (animate()) {
      anime({ targets: current, translateY: 0, duration: 300, easing: EASE_IN });
      anime({ targets: bg, opacity: 1, duration: 200, easing: 'easeOutQuad' });
    } else {
      current.style.transform = '';
      bg.style.opacity = '';
    }
  };
  stage.addEventListener('pointerup', endDrag);
  stage.addEventListener('pointercancel', endDrag);

  gallery.addEventListener('click', (e) => {
    const button = e.target.closest('[data-lightbox-open]');
    if (!button) return;
    const i = items.findIndex((item) => item.button === button);
    if (i >= 0) open(i);
  });
  dialog.addEventListener('click', (e) => {
    if (e.target.closest('[data-lightbox-close]')) close();
    else if (e.target.closest('[data-lightbox-prev]')) go(-1);
    else if (e.target.closest('[data-lightbox-next]')) go(1);
    else if (performance.now() - dragEnd < 500) return; // the click a swipe releases
    else if (e.target.classList.contains('lightbox__image')) { if (pointerType === 'mouse') close(); else go(e.clientX < window.innerWidth / 2 ? -1 : 1, { swipe: true }); } // a finger on the look moves on too: its own halves are the easy targets on a phone
    else if (e.target.closest('[data-lightbox-zone]')) go(Number(e.target.closest('[data-lightbox-zone]').dataset.lightboxZone), { swipe: pointerType !== 'mouse' }); // either side of the look
  });
  stage.querySelectorAll('[data-lightbox-zone]').forEach((zone) => zone.addEventListener('click', () => {})); // iOS taps only elements that listen (or an ancestor that does); no doubt this way
  dialog.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
  });
  dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); }); // Escape uses the same choreography
})();
